// e-AWB (electronic Air Waybill) types & constants.
// Ported from ifms3.3 iLogisys-FMS/fms.core.netcore (Pims/Eawb.cs, Pims/SuntekEdi/*,
// Constants/EawbType.cs, Constants/EAwbStatus.cs, Constants/CompanyIdType.cs).
// Field names are camelCase to match the .NET camelCase JSON policy used elsewhere
// in the api (see fms.core/pims/PimsCustomer.ts).

/** EDI broker type. Maps to ifms3.3 EawbBrokerType. */
export enum EawbBrokerType {
  GlsHk = 3,
  Cci = 4,
}

/** EDI message type string (broker x document). Maps to ifms3.3 EawbType. */
export enum EawbType {
  GlsFwb = 'GLSFWB',
  GlsFhl = 'GLSFHL',
  GlsFsu = 'GLSFSU',
  GlsFma = 'GLSFMA',
  GlsFna = 'GLSFNA',
  CciFwb = 'CCIFWB',
  CciFhl = 'CCIFHL',
  CciFsu = 'CCIFSU',
  CciFma = 'CCIFMA',
  CciFna = 'CCIFNA',
}

/** EDI status response code. Maps to ifms3.3 EawbEdiStatusResponse. */
export enum EawbEdiStatusResponse {
  NotSent = '',
  Pending = 'P',
  Sent = 'S',
  Accepted = 'A',
  Rejected = 'R',
  Amended = 'C',
  Deleted = 'D',
  Error = 'E',
}

/**
 * suntekedi 이력의 2자리 StatusDetail 코드 → 3자리 표기.
 * PIMS EDI Server Framework.EdiService/Constants/StatusDetail.cs Collection 대응
 * (CCI 시절 2자리 코드가 DB에 저장돼 있어 GLS HK 3자리 표기로 변환해 표시).
 */
export const EdiStatusDetailText: Record<string, string> = {
  RS: 'RCS',
  RF: 'RCF',
  BD: 'BKD',
  DD: 'DEP',
  DU: 'DEP(+ULD)',
  AD: 'ARR',
  CD: 'CCD',
  ND: 'NFD',
  DV: 'DLV',
  FH: 'FOH',
  FN: 'FNA',
  FM: 'FMA',
};

/** FRT Status(F_Fsu) 코드 → 설명 문구. EAwbStatus.cs FreightStatusCode.Collection 대응 (목록 툴팁 등). */
export const FreightStatusText: Record<string, string> = {
  DIS: 'DISCREPANCY',
  AWD: 'DOC DELIVERED',
  NFD: 'ARRIVAL NOTIFIED',
  ARR: 'ARRIVED',
  BKD: 'BOOKED',
  CCD: 'CUSTOMS CLEARED',
  TRM: 'TO BE TRANSFERRED TO ANOTHER AIRLINE',
  MAN: 'MANIFESTED',
  DLV: 'DELIVERED',
  DDL: 'DELIVERED TO DOOR',
  RCF: 'RCVD FROM FLIGHT',
  RCS: 'RCVD FROM SHIPPER',
  RCT: 'RCVD FROM ANOTHER AIRLINE',
  TFD: 'TRANSFERRED TO ANOTHER AIRLINE',
  PRE: 'PREPARED FOR LOADING',
  CRC: 'REPORTED TO CUSTOMS',
  TGC: 'TRANSFERRED TO CUSTOMS',
  DEP: 'DEPARTED',
  FOH: 'FREIGHT ON HAND',
  AWR: 'ARRIVAL DOC RCVD',
  FNA: 'REJECT',
  FMA: 'ACKNOWLEDGED',
};

/** 상태 코드 → 표시 문구. EAwbStatus.cs EawbEdiStatusResponse.Collection 대응 (목록 FWB/FHL 컬럼 등). */
export const EawbEdiStatusText: Record<string, string> = {
  [EawbEdiStatusResponse.NotSent]: 'Not Sent',
  [EawbEdiStatusResponse.Pending]: 'Requested',
  [EawbEdiStatusResponse.Sent]: 'Sent',
  [EawbEdiStatusResponse.Accepted]: 'Accepted',
  [EawbEdiStatusResponse.Rejected]: 'Rejected',
  [EawbEdiStatusResponse.Amended]: 'Amending',
  [EawbEdiStatusResponse.Deleted]: 'Deleted',
  [EawbEdiStatusResponse.Error]: 'Error',
};

/** Company / tax id type. Maps to ifms3.3 CompanyIdType. */
export enum CompanyIdType {
  Ein = 'E',  // US IRS Employer ID
  Usci = 'U', // Unified Social Credit Identifier (China)
  Oc = 'O',   // Organization Code (China)
  Eori = 'R', // Economic Operators' Registration and Identification (EU)
}

/** EDI broker account (SuntekEDI T_EdiAccount). */
export interface EdiAccount {
  id: number;
  pimsID?: string;
  brokerType: number;
  senderId: string;
  companyId?: string;
  suntekId?: string;
}

/** EDI FTP configuration (SuntekEDI T_EdiAwbFtp). */
export interface EdiAwbFtp {
  id: string;
  senderId: string;
  userId: string;
  password: string;
}

/** FTP credential info returned for a sender (EawbFtpInfo). */
export interface EawbFtpInfo {
  id: number;
  brokerType: number;
  senderID: string;
  companyID: string;
  ftpId: number;
  userID: string;
  password: string;
}

/** One transmission history record (EawbHistory). */
export interface EawbHistory {
  id: number;
  eventDate: string | null;
  type: string;       // FWB | FHL
  hawbNo: string;
  status: string;     // EawbEdiStatusResponse
  statusMessage: string;
  shipper: string;
  consignee: string;
}

/** Transmission request (AOEdiRequest) sent to the EDI server. */
export interface AOEdiRequest {
  id?: number;
  suntekId: string;
  f_MawbNo: string;
  f_RefNo: string;
  f_FltNo: string;
  f_LCode: string;
  f_DCode: string;
  f_To: string;
  f_To1: string;
  f_To2: string;
  f_BlType: string;
  f_Etd: string | null;
  f_Eta: string | null;
  f_Shipper?: number;
  f_Consignee?: number;
  f_SName: string;
  f_CName: string;
  connString?: string;
  ediType: string;          // EawbType
  srcJobName: string;       // AOMP (FWB) | AOHP (FHL)
  srcJobId: number;
  status: string;           // 'P'
  ediStatus: string;        // 'RTS'
  chargeCode: string;
  selectFhlIds: string;     // comma separated checked FHL ids (FHL only)
  createdBy?: string;
  modifiedBy?: string;
}

/** Transmission status (AOEdiStatus). */
export interface AOEdiStatus {
  suntekId: string;
  f_MawbNo: string;
  f_RefNo: string;
  srcJobId: number;
  f_BlType: string;
  f_FltNo: string;
  f_Etd: string | null;
  f_Eta: string | null;
  f_LCode: string;
  f_DCode: string;
  f_To: string;
  f_To1: string;
  f_To2: string;
  f_SName: string;
  f_CName: string;
  lastEdiTypeReq: string;   // EawbType
  statusLastAction: string; // 'P'
  statusFwb: string;        // EawbEdiStatusResponse
  statusFhl: string;        // EawbEdiStatusResponse
}

/** House Air Waybill row (Eawb.Fhl). */
export class Fhl {
  checked: boolean = false;
  id: number = 0;
  hawbNo: string = '';
  lastEdi: string | null = null;
  totalPieces: number = 0;
  grossWt: number = 0;
  natureOfGoods: string = '';
  description: string = '';
  shipper: number = 0;
  consignee: number = 0;

  sContact: string = '';
  sPhone: string = '';
  sFax: string = '';
  sEmail: string = '';
  sfName: string = '';
  sAddr: string = '';
  sCity: string = '';
  sState: string = '';
  sZipCode: string = '';
  sCompanyType: string = '';
  sCompanyId: string = '';
  sCountry: string = '';

  cContact: string = '';
  cPhone: string = '';
  cFax: string = '';
  cEmail: string = '';
  cfName: string = '';
  cAddr: string = '';
  cCity: string = '';
  cState: string = '';
  cZipCode: string = '';
  cCompanyType: string = '';
  cCompanyId: string = '';
  cCountry: string = '';

  hs6Codes: string = '';
  // 백엔드(Fhl.Hs6Codes)는 CRLF 만 줄 구분자로 봐서 LF 저장분을 못 자른다 →
  // 프론트에서 applyEawbReadDataFields 가 description 으로 다시 계산해 채운다.
  invalidHS6Code: boolean = false;
  u2Date: string | null = null;

  constructor(init?: Partial<Fhl>) {
    Object.assign(this, init);
  }
}

/** Master Air Waybill / e-AWB transmit entity (Eawb). */
export class Eawb {
  id: number = 0;
  mawbNo: string = '';
  prefix: string = '';
  awbNo: string = '';
  refNo: string = '';
  blType: string = '';
  origin: string = '';
  loadingPort: string = '';
  destination: string = '';
  dischargePort: string = '';
  totalPieces: number = 0;
  grossWeight: number = 0;
  fltNo: string = '';
  fltPrefix: string = '';
  fltNoNoPrefix: string = '';
  etd: string | null = null;
  eta: string | null = null;

  rTo: string = '';
  rTo1: string = '';
  rBy1: string = '';
  rEtd: string | null = null;
  rTo2: string = '';
  rBy2: string = '';
  rEtd2: string | null = null;

  shipper: number = 0;
  consignee: number = 0;
  agent: number = 0;
  iataCode: string = '';
  issuingCarrier: number = 0;
  icSName: string = '';
  icFName: string = '';
  icPlace: string = '';
  issueDate: string | null = null;
  issuePlace: string = '';
  accountInfo: string = '';
  carrierSpotNo: string = '';
  chargeCode: string = '';
  description: string = '';

  sContact: string = '';
  sPhone: string = '';
  sFax: string = '';
  sEmail: string = '';
  ssName: string = '';
  sfName: string = '';
  sAddr: string = '';
  sCity: string = '';
  sState: string = '';
  sZipCode: string = '';
  sCompanyType: string = '';
  sCompanyId: string = '';
  // 표시용: 접두(EIN/USCI/OC) 붙은 Company ID. 백엔드 Eawb.DisplaySCompanyId(FormatAwbCompanyId)
  // computed 프로퍼티가 직렬화한 값. 검증은 raw sCompanyId 사용(데스크톱 EAwbView 동일).
  displaySCompanyId: string = '';
  sCountry: string = '';

  cContact: string = '';
  cPhone: string = '';
  cFax: string = '';
  cEmail: string = '';
  csName: string = '';
  cfName: string = '';
  cAddr: string = '';
  cCity: string = '';
  cState: string = '';
  cZipCode: string = '';
  cCompanyType: string = '';
  cCompanyId: string = '';
  displayCCompanyId: string = '';
  cCountry: string = '';

  isIcs2: boolean = false;
  isIcs2NotEU: boolean = false;
  hs6Codes: string = '';
  invalidHS6Code: boolean = false;
  ediType: number = 0;       // EawbBrokerType
  u2Date: string | null = null;
  securityStatement: string = '';

  fhlList: Fhl[] = [];

  constructor(init?: Partial<Eawb>) {
    Object.assign(this, init);
    if (init?.fhlList) {
      this.fhlList = init.fhlList.map(f => new Fhl(f));
    }
  }
}
